import React, { useRef, useState, useEffect, ReactNode, forwardRef, useImperativeHandle } from 'react';

interface CustomScrollbarProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 自定义滚动条组件，用于大页面
 * 特点：
 * 1. 滚动条浮动在内容之上，不占用布局空间
 * 2. 滚动条背景完全透明
 * 3. 支持鼠标拖拽和滚轮滚动
 */
const CustomScrollbar = forwardRef<HTMLDivElement, CustomScrollbarProps>(({ children, className, style }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  
  // 暴露内部的 container ref 给父组件
  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);
  
  const [isDragging, setIsDragging] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  // 计算滚动条的显示和位置
  const updateScrollbar = () => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const containerHeight = container.clientHeight;
    const contentHeight = content.scrollHeight;
    const scrollTop = container.scrollTop;

    // 判断是否需要显示滚动条
    const needsScrollbar = contentHeight > containerHeight;
    setShowScrollbar(needsScrollbar);

    if (needsScrollbar) {
      // 计算滚动条滑块的高度（按比例）
      const thumbHeightCalc = Math.max(
        (containerHeight / contentHeight) * containerHeight,
        30 // 最小高度
      );
      setThumbHeight(thumbHeightCalc);

      // 计算滚动条滑块的位置
      const scrollPercentage = scrollTop / (contentHeight - containerHeight);
      const maxThumbTop = containerHeight - thumbHeightCalc;
      setThumbTop(scrollPercentage * maxThumbTop);
    }
  };

  // 监听滚动事件
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateScrollbar();
    };

    container.addEventListener('scroll', handleScroll);
    updateScrollbar();

    // 监听窗口大小变化
    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(container);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [children]);

  // 处理滚动条拖拽
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = containerRef.current?.scrollTop || 0;
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current;
      const content = contentRef.current;
      if (!container || !content) return;

      const deltaY = e.clientY - dragStartY.current;
      const containerHeight = container.clientHeight;
      const contentHeight = content.scrollHeight;
      const scrollRange = contentHeight - containerHeight;
      const thumbScrollRange = containerHeight - thumbHeight;

      const scrollDelta = (deltaY / thumbScrollRange) * scrollRange;
      container.scrollTop = dragStartScrollTop.current + scrollDelta;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // 拖拽结束时，强制恢复默认颜色
      if (thumbRef.current) {
        thumbRef.current.style.backgroundColor = 'rgba(160, 160, 160, 0.3)';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, thumbHeight]);

  // 点击滚动条轨道
  const handleTrackClick = (e: React.MouseEvent) => {
    if (e.target !== scrollbarRef.current) return;
    
    const container = containerRef.current;
    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;
    if (!container || !content || !scrollbar) return;

    const rect = scrollbar.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const containerHeight = container.clientHeight;
    const contentHeight = content.scrollHeight;
    const scrollRange = contentHeight - containerHeight;

    const scrollPercentage = (clickY - thumbHeight / 2) / (containerHeight - thumbHeight);
    container.scrollTop = scrollPercentage * scrollRange;
  };

  return (
    <div
      className={className}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 内容容器 */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE
        }}
        className="custom-scrollbar-container"
      >
        <div ref={contentRef}>
          {children}
        </div>
      </div>

      {/* 隐藏原生滚动条 */}
      <style>{`
        .custom-scrollbar-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* 自定义滚动条 */}
      {showScrollbar && (
        <div
          ref={scrollbarRef}
          onClick={handleTrackClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 8,
            height: '100%',
            cursor: 'pointer',
            zIndex: 999,
            pointerEvents: 'all',
          }}
        >
          {/* 滚动条滑块 */}
          <div
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            style={{
              position: 'absolute',
              top: thumbTop,
              left: 0,
              width: '100%',
              height: thumbHeight,
              backgroundColor: 'rgba(160, 160, 160, 0.3)',
              borderRadius: 10,
              cursor: isDragging ? 'grabbing' : 'grab',
              transition: isDragging ? 'none' : 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(160, 160, 160)';
            }}
            onMouseLeave={(e) => {
              if (!isDragging) {
                e.currentTarget.style.backgroundColor = 'rgba(160, 160, 160, 0.3)';
              }
            }}
          />
        </div>
      )}
    </div>
  );
});

CustomScrollbar.displayName = 'CustomScrollbar';

export { CustomScrollbar };
export type { CustomScrollbarProps };
